import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { BackendAPI } from '@app/core/models/backend-api';
import { Package } from '@app/core/models/package.model';
import { PlacementCategory } from '@app/core/models/placement-category.model';
import { Placement } from '@app/core/models/placement.model';
import { Style } from '@app/core/models/style.model';
import { OrdersService } from '@app/core/services/orders.service';

type StaticDataType = "CATEGORY" | "PLACEMENT" | "PACKAGE" | "STYLE" | "DATE";

class TestTask {
	public url?: string;
	public httpStatus?: number;
	public result?: boolean;
	public message?: string;
	public durationMillis?: number;
}

class StaticDataTree {
	public indent: string;
	public noStaticData: boolean = false;
	public children: StaticDataTree[] = [];
	public label: string;
	public type: StaticDataType;
	constructor(label: string, type: StaticDataType, lastPlacement: boolean, lastPackage: boolean, lastStyle: boolean) {
		const SPACE = "&nbsp;";
		const TAB = SPACE.repeat(4);
		const VPIPE = "│";
		const HPIPE = "─";
		const CORNER = "└";
		const TEE = "├";
		this.label = label;
		this.type = type;
		this.indent = "";
		if (label.length === 0) {
			this.noStaticData = true;
			this.label = "NO " + type + "S!";
		}
		if (type === 'STYLE' || type === 'DATE') {
			this.indent = TAB + (lastPlacement ? SPACE : VPIPE) + TAB + (lastPackage ? SPACE : VPIPE) + TAB + (lastStyle ? CORNER : TEE) + HPIPE;
		}
		if (type === 'PACKAGE') {
			this.indent = TAB + (lastPlacement ? SPACE : VPIPE) + TAB + (lastPackage ? CORNER : TEE) + HPIPE;
		}
		if (type === 'PLACEMENT') {
			this.indent = TAB + (lastPlacement ? CORNER : TEE) + HPIPE;
		}
	};
}

@Component({
	selector: 'app-self-test',
	templateUrl: './self-test.component.html',
	styleUrls: ['./self-test.component.css']
})
export class SelfTestComponent implements OnInit {

	public testTasks: TestTask[] = [];

	public staticDataRoot: StaticDataTree[] = [];

	public masterStatus?: boolean;

	constructor(
		private http: HttpClient
		) {}

	async ngOnInit(): Promise<void> {
		await this.doStaticData();
		this.masterStatus = !this.testTasks.some(x => x.result == false);
	}

	private async doStaticData() {
		// Categories
		const categories = await this.getCategories();
		// Categories -> Placements
		for (let category of categories) {
			let categoryNode = new StaticDataTree(category.label!, "CATEGORY", false, false, false);
			this.staticDataRoot.push(categoryNode);
			const placements: Placement[] = category.placements ?? [];
			if (placements.length == 0) {
				categoryNode.children.push(new StaticDataTree("", "PLACEMENT", true, true, true));
			}
			else {
				placements.map(async (placement, placementIndex, placementArray) => {
					const lastPlacement = placementIndex === placementArray.length - 1;
					let placementNode = new StaticDataTree(placement.label!, "PLACEMENT", lastPlacement, false, false);
					categoryNode.children.push(placementNode);
					// Placements -> Packages
					const pkgs = await this.getPackagesForPlacement(placement);
					if (pkgs.length == 0) {
						placementNode.children.push(new StaticDataTree("", "PACKAGE", lastPlacement, true, true));
					}
					else {
						pkgs.map(async (pkg, pkgIndex, pkgArray) => {
							const lastPackage = pkgIndex === pkgArray.length - 1;
							let packageNode = new StaticDataTree(pkg.label, "PACKAGE", lastPlacement, lastPackage, false);
							placementNode.children.push(packageNode);
							// Packages -> Dates
							this.getDatesForPlacementAndPackage(placement, pkg)
								.then(dates => {
									if (dates.length == 0) {
										packageNode.children.push(new StaticDataTree("", "DATE", lastPlacement, lastPackage, false));
									}
								});
							// Packages -> Styles
							this.getStylesForPlacementAndPackage(placement, pkg)
								.then(styles => {
									if (styles.length == 0) {
										packageNode.children.push(new StaticDataTree("", "STYLE", lastPlacement, lastPackage, true));
									}
									else {
										for (let [styleIndex, style] of styles.entries()) {
											packageNode.children.push(new StaticDataTree(style.label!, "STYLE", lastPlacement, lastPackage, styleIndex === styles.length - 1));
										}
									}
								});
						});
					}
				});
			}
		}
	}

	private async createGetStaticDataArrayTask<T>(url: string): Promise<T[]> {
		let task = new TestTask();
		this.testTasks.push(task);
		task.url = "GET " + url;
		var startTime = new Date();
		const response = await this.http.get(url , {observe: "response"}).toPromise();
		task.durationMillis = (new Date()).valueOf() - startTime.valueOf();
		task.result = false;
		task.httpStatus = response?.status;
		if (response && response.ok) {
			const results = response.body as T[];
			if (results.length > 0) {
				task.result = true;
				task.message = "(" + results.length + "): " + JSON.stringify(results, null, " ");
				return results;
			}
			else {
				task.message = "(0): No results";
			}
		}
		else {
			task.message = "HTTP error";
		}
		return [];
	}

	private async getCategories(): Promise<PlacementCategory[]> {
		const url = BackendAPI.staticDataPlacementCategories;
		return this.createGetStaticDataArrayTask(url);
	}

	private async getPackagesForPlacement(placement: Placement): Promise<Package[]> {
		const url = BackendAPI.staticDataPackagesForPlacement + "/" + placement.id;
		return this.createGetStaticDataArrayTask(url);
	}

	private async getStylesForPlacementAndPackage(placement: Placement, pkg: Package): Promise<Style[]> {
		const url = BackendAPI.staticDataStylesForPackageAndPlacement + "?placementId=" + placement.id + "&packageId=" + pkg.id;
		return this.createGetStaticDataArrayTask(url);
	}

	private async getDatesForPlacementAndPackage(placement: Placement, pkg: Package): Promise<Style[]> {
		const today = new Date();
		const url = BackendAPI.staticDataDatesForPackageAndPlacement
			+ "?placementId=" + placement.id
			+ "&packageId=" + pkg.id
			+ "&startMonth=" + (today.getMonth()+1)
			+ "&startYear=" + today.getFullYear()
			+ "&monthCount=2";
		return this.createGetStaticDataArrayTask(url);
	}

}
